/**
 * Japan labour-market facts used on the Home and Work in Japan pages.
 * Every number has a public source (see SOURCES). Update when new figures are published:
 *  - foreign workers: MHLW, every January (figures as of end of October)
 *  - SSW holders: Immigration Services Agency, twice a year
 */
type T = { en: string; ja: string }

export interface KeyStat {
  value: { en: number; ja: number }
  decimals: { en: number; ja: number }
  suffix: T
  label: T
  note: T
  source: number // index in SOURCES
}

export const SOURCES = [
  { name: 'Recruit Works Institute (2023), via Bloomberg', url: 'https://www.bloomberg.com/news/articles/2023-03-29/japan-to-face-11-million-worker-shortfall-by-2040-study-finds' },
  { name: 'Ministry of Health, Labour and Welfare (Oct 2025), via nippon.com', url: 'https://www.nippon.com/en/news/yjj2026013000531' },
  { name: 'Cabinet decision on SSW & Employment for Skill Development caps (Dec 2025), via nippon.com', url: 'https://www.nippon.com/en/news/yjj2025122300443' },
  { name: 'National Institute of Population and Social Security Research (2023), via nippon.com', url: 'https://www.nippon.com/en/japan-data/h01664/' },
  { name: 'JICA Ogata Research Institute (2022)', url: 'https://www.jica.go.jp/english/jica_ri/news/topics/2022/20220203_02.html' },
  { name: 'Immigration Services Agency (end of 2025), via nippon.com', url: 'https://www.nippon.com/en/japan-data/h02750/' },
  { name: 'Ministry of Foreign Affairs of Japan (23 Dec 2019)', url: 'https://www.mofa.go.jp/press/release/press4e_002741.html' },
  { name: 'Ministry of Internal Affairs and Communications (Sep 2026), via The Star', url: 'https://www.thestar.com.my/aseanplus/aseanplus-news/2026/09/20/japan039s-elderly-population-hits-a-record-296-per-cent-highest-among-major-nations' },
]

export const KEY_STATS: KeyStat[] = [
  {
    value: { en: 11, ja: 1100 },
    decimals: { en: 0, ja: 0 },
    suffix: { en: 'M', ja: '万人' },
    label: { en: 'workers Japan will be short of by 2040', ja: '2040年に不足すると予測される働き手' },
    note: { en: 'Projected labour shortfall', ja: '労働力不足の予測' },
    source: 0,
  },
  {
    value: { en: 2.57, ja: 257 },
    decimals: { en: 2, ja: 0 },
    suffix: { en: 'M', ja: '万人' },
    label: { en: 'foreign workers in Japan, a new record (+11.7% in a year)', ja: '日本で働く外国人（過去最多、前年比+11.7%）' },
    note: { en: 'October 2025', ja: '2025年10月末' },
    source: 1,
  },
  {
    value: { en: 1.23, ja: 123 },
    decimals: { en: 2, ja: 0 },
    suffix: { en: 'M', ja: '万人' },
    label: { en: 'foreign workers Japan plans to accept through SSW and the new skills system by FY2028', ja: '2028年度までに特定技能・育成就労で受け入れ予定の上限' },
    note: { en: 'Government cap', ja: '政府の受け入れ上限' },
    source: 2,
  },
  {
    value: { en: 29.6, ja: 29.6 },
    decimals: { en: 1, ja: 1 },
    suffix: { en: '%', ja: '%' },
    label: { en: 'of Japan’s population is aged 65 or over, the highest of any major nation', ja: '65歳以上が人口に占める割合（主要国で最高）' },
    note: { en: 'September 2026', ja: '2026年9月' },
    source: 7,
  },
]

/** Foreign workers in Japan, end of October each year (MHLW) */
export const FOREIGN_WORKERS = [
  { year: 2015, value: 907_896 },
  { year: 2016, value: 1_083_769 },
  { year: 2017, value: 1_278_670 },
  { year: 2018, value: 1_460_463 },
  { year: 2019, value: 1_658_804 },
  { year: 2020, value: 1_724_328 },
  { year: 2021, value: 1_727_221 },
  { year: 2022, value: 1_822_725 },
  { year: 2023, value: 2_048_675 },
  { year: 2024, value: 2_302_587 },
  { year: 2025, value: 2_571_037 },
]

/** Working-age population (15–64), millions. Census to 2020, IPSS 2023 medium projection after. */
export const WORKING_AGE = [
  { year: 1995, value: 87.3, projected: false },
  { year: 2005, value: 84.4, projected: false },
  { year: 2015, value: 77.3, projected: false },
  { year: 2020, value: 75.1, projected: false },
  { year: 2030, value: 70.8, projected: true },
  { year: 2040, value: 62.1, projected: true },
  { year: 2050, value: 55.4, projected: true },
  { year: 2060, value: 50.8, projected: true },
  { year: 2070, value: 45.4, projected: true },
]

export const MORE_FACTS = [
  {
    value: '390,296',
    label: { en: 'Specified Skilled Workers living in Japan at the end of 2025, up 37.2% in a year', ja: '2025年末の特定技能外国人（1年で37.2%増）' },
    source: 5,
  },
  {
    value: '6.74M',
    label: { en: 'foreign workers Japan would need in 2040 to reach its growth target, about 4× today’s level at the time of the study', ja: '成長目標の達成に2040年に必要とされる外国人労働者（調査時の約4倍）' },
    source: 4,
  },
  {
    value: '2019',
    label: { en: 'Japan and Pakistan signed a Memorandum of Cooperation on Specified Skilled Workers (23 December)', ja: '日本とパキスタンが特定技能に関する協力覚書に署名（12月23日）' },
    source: 6,
  },
]

export type Lang = 'en' | 'ja'
export const t = (v: T, l: Lang) => v[l]
